import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TbPlus, TbTrash, TbChevronLeft } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import {
  CreateBankTransactionModel,
  Currency,
  JournalEntryModel,
  JOURNAL_ENTRY_SOURCE_TYPES,
  LedgerAccount,
  LedgerAccountType,
} from '../../../types/bankAccount';
import { RootState } from '../../../redux/reducers';
import PageHeader from '../../components/misc/PageHeader';
import { PaidByInfo, PaidForInfo } from '../../components/financial/DeclarationInfo';
import SearchBar from '../../components/forms/SearchBar';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import {
  FinancialTable,
  FinancialDataCell,
} from '../../components/tables/FinancialTable';
import { formatDate } from '../../../utils/stringUtils';
import * as bankAccountService from '../../../services/community/bankAccountService';
import DeclarationModal from '../../components/modals/DeclarationModal';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { transactionToJournalEntryDto, calculateDisplayAmount, formatBalanceWithSign } from '../../../utils/bankAccountUtil';
import Button from '../../components/buttons/Button';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import connectionService from '../../../services/community/connectionService';

interface DeclarationsPageProps {
  hideHeader?: boolean;
}

export default function DeclarationsPage({
  hideHeader = false,
}: DeclarationsPageProps): JSX.Element | null {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.declaration' });
  const { t: tEmpty } = useTranslation('', { keyPrefix: 'page.financial.declaration.emptyState' });
  const [declarations, setDeclarations] = useState<JournalEntryModel[] | null>(null);
  const connections = useSelector((state: RootState) => state.connections);
  const community = useSelectedCommunity();
  const [search, setSearch] = useState<string>('');
  const [splitModalDeclaration, setSplitModalDeclaration] = useState<JournalEntryModel | null>(null);
  const [splitModalOpen, setSplitModalOpen] = useState<boolean>(false);
  const [createDeclarationModalOpen, setCreateDeclarationModalOpen] = useState<boolean>(false);

  const dashboardId = useSelector((state: RootState) => state.application.selectedDashboard);

  const getDeclarationEntries = (journalEntries: JournalEntryModel[]) =>
    journalEntries.filter(
      (entry) =>
        entry.payerId &&
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE,
    );

  useEffect(() => {
    bankAccountService.getAllJournalEntries().then((journalEntries) => {
      setDeclarations(getDeclarationEntries(journalEntries));
    });
    connectionService.getConnections();
  }, [community]);

  const openDeclarationModal = (declaration: JournalEntryModel) => {
    setSplitModalDeclaration(declaration);
    setTimeout(() => setSplitModalOpen(true), 10);
  };

  if (!community) return null;

  return (
    <>
      {!hideHeader ? (
        <>
          <Link to="/financial/transactions">
            <Button className="mb-2 gap-1 p-0">
              <TbChevronLeft size={20} /> Back
            </Button>
          </Link>
          <PageHeader
            title={t('title')}
            subtitle={t('subtitle')}
            primaryButton={
              declarations && declarations.length > 0 ? (
                <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                  <TbPlus /> {t('addDeclaration')}
                </Button>
              ) : undefined
            }
          />
        </>
      ) : (
        <div className="mb-4 flex items-center justify-between">
          {declarations && declarations.length > 0 && (
            <>
              <SearchBar className="max-w-[380px]" search={search} setSearch={setSearch} />
              <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                <TbPlus /> {t('addDeclaration')}
              </Button>
            </>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-5 pb-4">
        {!declarations ? (
          <EmptyScreenView subtitle="" />
        ) : declarations.length === 0 ? (
          <EmptyScreenView
            title={tEmpty('title')}
            subtitle={tEmpty('subtitle')}
            primaryButton={
              <Button variant="primary-dark" onClick={() => setCreateDeclarationModalOpen(true)}>
                <TbPlus /> {tEmpty('createFirstButton')}
              </Button>
            }
          />
        ) : (
          <>
            <div className="flex flex-col gap-8">
              <FinancialTable
                columns={[
                  { id: 'date', label: t('table.date') },
                  { id: 'description', label: t('table.description') },
                  { id: 'amount', label: t('table.amount') },
                  { id: 'paidBy', label: t('table.paidBy') },
                  { id: 'paidFor', label: t('table.paidFor') },
                  { id: 'actions', label: t('table.actions') },
                ]}
                rows={declarations.filter((declaration) => {
                  if (search.trim()) {
                    const searchLower = search.toLowerCase();
                    const descriptionMatch = declaration.description?.toLowerCase().includes(searchLower);
                    
                    // Check journal entry lines for participant member IDs
                    const participantMatch = declaration.lines?.some((line: { participantMemberId: number | null }) => {
                      if (line.participantMemberId) {
                        const connection = connections.find(
                          (c) => +c.dashboardMemberId === line.participantMemberId,
                        );
                        if (connection) {
                          const fullName =
                            `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`.toLowerCase();
                          return fullName.includes(searchLower);
                        }
                      }
                      return false;
                    });
                    
                    return descriptionMatch || participantMatch;
                  }
                  return true;
                })}
                renderRow={(declaration: JournalEntryModel) => {
                  // Calculate display amount using the utility function
                  const displayAmount = calculateDisplayAmount(declaration);
                  
                  return (
                    <>
                      <FinancialDataCell onClick={() => openDeclarationModal(declaration)}>
                        {formatDate(new Date(declaration.entryDate))}
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openDeclarationModal(declaration)}>
                        <p className="text-sm">
                          {declaration.description.slice(0, 50)}
                          {declaration.description.length >= 50 && '...'}
                        </p>
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openDeclarationModal(declaration)}>
                        {formatBalanceWithSign(displayAmount, Currency.EUR, i18n.language)}
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openDeclarationModal(declaration)}>
                        <PaidByInfo declaration={declaration as any} />
                      </FinancialDataCell>
                      <FinancialDataCell onClick={() => openDeclarationModal(declaration)}>
                        <PaidForInfo declaration={declaration as any} />
                      </FinancialDataCell>
                      <FinancialDataCell>
                        <ActionMenu>
                          <ActionMenuButton
                            icon={TbTrash}
                            text={t('table.delete')}
                            className="text-danger"
                            onClick={() => {
                              bankAccountService
                                .deleteJournalEntry(declaration.journalEntryId)
                                .then(() => {
                                  setDeclarations((prev) =>
                                    prev
                                      ? prev.filter(
                                        (decl) =>
                                          decl.journalEntryId !== declaration.journalEntryId,
                                      )
                                      : prev,
                                  );
                                  toast.success(t('success.deleted'));
                                })
                                .catch((error) => {
                                  toast.error(
                                    t('error.deletionFailed', { error: error.message }),
                                  );
                                });
                            }}
                          />
                        </ActionMenu>
                      </FinancialDataCell>
                    </>
                  );
                }}
              />
            </div>
          </>
        )}

        {splitModalDeclaration && (
          <DeclarationModal
            open={splitModalOpen}
            setOpen={(open) => {
              if (open) {
                setSplitModalDeclaration(splitModalDeclaration);
                setTimeout(() => setSplitModalOpen(true), 10);
              } else {
                setSplitModalOpen(false);
                setTimeout(() => setSplitModalDeclaration(null), 200);
              }
            }}
            initialTransaction={splitModalDeclaration as any}
            onSplitSuccess={(declaration: CreateBankTransactionModel) => {
              if (!splitModalDeclaration || !dashboardId) return;

              bankAccountService
                .getWalletAccounts()
                .then((accounts: LedgerAccount[]) => {
                  const walletAccount = accounts.find(
                    (acc) => acc.accountType === LedgerAccountType.ASSET && acc.dashboardMemberId === null,
                  );
                  const expenseAccount = accounts.find(
                    (acc) => acc.accountType === LedgerAccountType.EXPENSE,
                  );
                  const incomeAccount = accounts.find(
                    (acc) => acc.accountType === LedgerAccountType.REVENUE,
                  );
                  
                  const dto = transactionToJournalEntryDto(
                    declaration,
                    expenseAccount?.ledgerAccountId,
                    incomeAccount?.ledgerAccountId,
                    walletAccount?.ledgerAccountId,
                    Currency.EUR,
                  );
                  
                  return bankAccountService.updateJournalEntry(
                    splitModalDeclaration.journalEntryId,
                    { ...dto, dashboardId },
                  );
                })
                .then(() => {
                  return bankAccountService.getAllJournalEntries();
                })
                .then((journalEntries) => {
                  setDeclarations(getDeclarationEntries(journalEntries));
                  toast.success(t('success.updated'));
                  setSplitModalOpen(false);
                  setTimeout(() => setSplitModalDeclaration(null), 200);
                })
                .catch((error) => {
                  const errorMessage = error?.message || t('error.declarationCreation');
                  toast.error(errorMessage);
                  console.error('Failed to update declaration:', error);
                });
            }}
          />
        )}

        <DeclarationModal
          open={createDeclarationModalOpen}
          setOpen={setCreateDeclarationModalOpen}
          onSplitSuccess={(declaration: CreateBankTransactionModel) => {
            if (!dashboardId) {
              toast.error(t('error.declarationCreation'));
              return;
            }
            bankAccountService
              .getWalletAccounts()
              .then((accounts: LedgerAccount[]) => {
                const walletAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.ASSET && acc.dashboardMemberId === null,
                );
                const expenseAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.EXPENSE,
                );
                const incomeAccount = accounts.find(
                  (acc) => acc.accountType === LedgerAccountType.REVENUE,
                );
                
                const dto = transactionToJournalEntryDto(
                  declaration,
                  expenseAccount?.ledgerAccountId,
                  incomeAccount?.ledgerAccountId,
                  walletAccount?.ledgerAccountId,
                  Currency.EUR,
                );
                
                return bankAccountService.createJournalEntry({
                  ...dto,
                  dashboardId,
                });
              })
              .then(() => {
                return bankAccountService.getAllJournalEntries();
              })
              .then((journalEntries) => {
                setDeclarations(getDeclarationEntries(journalEntries));
                toast.success(t('success.created'));
                setCreateDeclarationModalOpen(false);
              })
              .catch((error) => {
                const errorMessage = error?.message || t('error.declarationCreation');
                toast.error(errorMessage);
                console.error('Failed to create declaration:', error);
              });
          }}
        />
      </div>
    </>
  );
}

